import { Controller } from "@hotwired/stimulus"

/**
 * Notes Drawer Controller
 *
 * Gère le panneau latéral de prise de notes accessible depuis un FAB.
 * Permet de lister, créer, modifier et rechercher des notes rapidement.
 *
 * RGAA 4.1 compliant avec focus trap implementation.
 */
export default class extends Controller {
  static targets = [
    "drawer",
    "overlay",
    "list",
    "formContainer",
    "searchInput",
    "fab",
    "helpPanel"
  ]

  static values = {
    notesUrl: String,
    newNoteUrl: String,
    isOpen: { type: Boolean, default: false }
  }

  static STORAGE_KEY = "notesDrawerOpen"

  // Délai anti-rebond pour la recherche (ms)
  static SEARCH_DEBOUNCE = 300

  static FOCUSABLE_SELECTORS = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    'input:not([type="hidden"]):not([disabled])',
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
    "lexxy-editor"
  ].join(", ")

  connect() {
    this.previouslyFocusedElement = null
    this.searchTimeout = null
    this.bindGlobalShortcut()
    this.bindFormSubmitHandler()
    this.restoreState()
  }

  disconnect() {
    this.unbindGlobalShortcut()
    this.unbindFormSubmitHandler()
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
  }

  // === Actions publiques ===

  toggle(event) {
    event?.preventDefault()
    this.isOpenValue ? this.close() : this.open()
  }

  open() {
    this.previouslyFocusedElement = document.activeElement
    this.isOpenValue = true
    this.drawerTarget.classList.remove("translate-x-full")
    this.drawerTarget.setAttribute("aria-hidden", "false")

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden")
    }
    if (this.hasFabTarget) {
      this.fabTarget.classList.add("hidden")
    }

    this.fetchNotesList()
    this.setupFocusTrap()
    this.focusFirstElement()
    this.saveState()
  }

  close() {
    this.isOpenValue = false
    this.drawerTarget.classList.add("translate-x-full")
    this.drawerTarget.setAttribute("aria-hidden", "true")

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden")
    }
    if (this.hasFabTarget) {
      this.fabTarget.classList.remove("hidden")
    }

    // Réinitialiser la vue : afficher la liste, masquer le formulaire
    this.showList()

    this.releaseFocusTrap()
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus()
      this.previouslyFocusedElement = null
    }
    this.saveState()
  }

  toggleHelp(event) {
    event?.preventDefault()
    if (!this.hasHelpPanelTarget) return

    const isHidden = this.helpPanelTarget.classList.toggle("hidden")
    event?.currentTarget?.setAttribute("aria-expanded", (!isHidden).toString())
  }

  // === Navigation interne ===

  showList() {
    if (this.hasListTarget) this.listTarget.classList.remove("hidden")
    if (this.hasFormContainerTarget) {
      this.formContainerTarget.classList.add("hidden")
      this.formContainerTarget.textContent = ""
    }
    if (this.hasHelpPanelTarget) this.helpPanelTarget.classList.add("hidden")
  }

  async openNewForm(event) {
    event?.preventDefault()
    await this.loadForm(this.newNoteUrlValue)
  }

  async openNote(event) {
    event?.preventDefault()
    const url = event.currentTarget.dataset.url
    if (url) await this.loadForm(url)
  }

  async openEditForm(event) {
    event?.preventDefault()
    const url = event.currentTarget.dataset.url ||
                event.currentTarget.getAttribute("href")
    if (url) await this.loadForm(url)
  }

  // === Recherche avec debounce ===

  search() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout)

    this.searchTimeout = setTimeout(() => {
      this.fetchNotesList()
    }, this.constructor.SEARCH_DEBOUNCE)
  }

  // === Fetch ===

  async fetchNotesList() {
    if (!this.notesUrlValue) return

    const url = new URL(this.notesUrlValue, window.location.origin)
    if (this.hasSearchInputTarget && this.searchInputTarget.value.trim()) {
      url.searchParams.set("q", this.searchInputTarget.value.trim())
    }

    const html = await this.fetchHtml(url.toString())
    if (html && this.hasListTarget) {
      this.replaceContent(this.listTarget, html)
    }
  }

  async loadForm(url) {
    const html = await this.fetchHtml(url)
    if (!html) return

    if (this.hasFormContainerTarget) {
      this.replaceContent(this.formContainerTarget, html)
      this.formContainerTarget.classList.remove("hidden")
    }
    if (this.hasListTarget) this.listTarget.classList.add("hidden")
  }

  async fetchHtml(url) {
    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" }
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      return await response.text()
    } catch (error) {
      console.error("Erreur chargement:", error)
      return null
    }
  }

  // === Focus Trap (RGAA 4.1) ===

  setupFocusTrap() {
    this.trapFocusHandler = this.trapFocus.bind(this)
    this.drawerTarget.addEventListener("keydown", this.trapFocusHandler)
  }

  releaseFocusTrap() {
    if (this.trapFocusHandler) {
      this.drawerTarget.removeEventListener("keydown", this.trapFocusHandler)
    }
  }

  trapFocus(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key !== "Tab") return

    const focusable = this.getFocusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  getFocusableElements() {
    return Array.from(
      this.drawerTarget.querySelectorAll(this.constructor.FOCUSABLE_SELECTORS)
    ).filter(el => el.offsetWidth > 0 && el.offsetHeight > 0)
  }

  focusFirstElement() {
    setTimeout(() => {
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.focus()
      } else {
        const elements = this.getFocusableElements()
        if (elements.length > 0) elements[0].focus()
      }
    }, 150)
  }

  // === Global Shortcut ===

  bindGlobalShortcut() {
    this.globalShortcutHandler = (event) => {
      // Cmd/Ctrl + Shift + N
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === "N") {
        event.preventDefault()
        this.toggle(event)
      }
    }
    document.addEventListener("keydown", this.globalShortcutHandler)
  }

  unbindGlobalShortcut() {
    if (this.globalShortcutHandler) {
      document.removeEventListener("keydown", this.globalShortcutHandler)
    }
  }

  // === State Persistence ===

  saveState() {
    sessionStorage.setItem(this.constructor.STORAGE_KEY, this.isOpenValue.toString())
  }

  restoreState() {
    const wasOpen = sessionStorage.getItem(this.constructor.STORAGE_KEY) === "true"
    if (wasOpen) this.open()
  }

  // === Form Submit Handler ===

  bindFormSubmitHandler() {
    this.formSubmitHandler = (event) => {
      if (!this.hasFormContainerTarget) return
      if (!this.formContainerTarget.contains(event.target)) return

      const { fetchResponse } = event.detail
      if (fetchResponse?.succeeded) {
        this.showList()
      }
    }
    this.element.addEventListener("turbo:submit-end", this.formSubmitHandler)
  }

  unbindFormSubmitHandler() {
    if (this.formSubmitHandler) {
      this.element.removeEventListener("turbo:submit-end", this.formSubmitHandler)
    }
  }

  // === Helpers ===

  /**
   * Remplace le contenu d'un élément de manière sécurisée.
   * Le HTML provient exclusivement du serveur Rails (contenu de confiance).
   */
  replaceContent(element, html) {
    const template = document.createElement("template")
    template.innerHTML = html
    element.replaceChildren(template.content)
  }
}
